<template><markdown-wrapper><h1 id="datepicker">DatePicker <a class="header-anchor" href="#datepicker">&#128279;</a></h1>
<blockquote>
<p>A lightweight &amp; configurable date picker.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>Use <code v-pre>v-model</code> to bind the selected date.</p>
<date-picker-example ref="date-picker-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;date&quot;</span>&gt;</span>You selected <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>{{date}}<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span>.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-example.vue --&gt;</span>
</code></pre>
<h2 id="formats">Formats <a class="header-anchor" href="#formats">&#128279;</a></h2>
<p>You can use any format you like. For example:</p>
<ul>
<li>yyyy-M-d</li>
<li>yyyy-MM-dd</li>
<li>yyyy-MMM-dd</li>
<li>yyyy-MMMM-dd</li>
<li>yyyy/MM/dd</li>
<li>MM/dd/yyyy</li>
<li>yyyy, MM, dd</li>
<li>...</li>
</ul>
<p>Note that some browsers (e.g. IE) might not support all of these formats.</p>
<p>If you need a special format that not supported by <code v-pre>Date.parse</code>, consider using <code v-pre>date-parser</code> option to override it.</p>
<date-picker-formats ref="date-picker-formats"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span> <span class="hljs-attr">format</span>=<span class="hljs-string">&quot;yyyy/MMMM/dd&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;date&quot;</span>&gt;</span>You selected <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>{{date}}<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span>.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-formats.vue --&gt;</span>
</code></pre>
<h2 id="buttons">Buttons <a class="header-anchor" href="#buttons">&#128279;</a></h2>
<p>Use <code v-pre>today-btn</code> and <code v-pre>clear-btn</code> to toggle visible of them.</p>
<date-picker-without-buttons ref="date-picker-without-buttons"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span> <span class="hljs-attr">:today-btn</span>=<span class="hljs-string">&quot;false&quot;</span> <span class="hljs-attr">:clear-btn</span>=<span class="hljs-string">&quot;false&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-without-buttons.vue --&gt;</span>
</code></pre>
<h2 id="range-limit">Range limit <a class="header-anchor" href="#range-limit">&#128279;</a></h2>
<p>Example that limit date range from <strong>2018-01-01</strong> to <strong>2019-01-01</strong>:</p>
<date-picker-range-limit ref="date-picker-range-limit"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span> <span class="hljs-attr">limit-from</span>=<span class="hljs-string">&quot;2018-01-01&quot;</span> <span class="hljs-attr">limit-to</span>=<span class="hljs-string">&quot;2019-01-01&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-range-limit.vue --&gt;</span>
</code></pre>
<h2 id="week-starts">Week starts <a class="header-anchor" href="#week-starts">&#128279;</a></h2>
<p>Change the starting day of the week. Support 0 (Sunday) ~ 6 (Saturday).</p>
<date-picker-week-starts ref="date-picker-week-starts"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span> <span class="hljs-attr">:week-starts-with</span>=<span class="hljs-string">&quot;1&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-week-starts.vue --&gt;</span>
</code></pre>
<h2 id="week-numbers">Week numbers <a class="header-anchor" href="#week-numbers">&#128279;</a></h2>
<date-picker-week-numbers ref="date-picker-week-numbers"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span> <span class="hljs-attr">week-numbers</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">br</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span> <span class="hljs-attr">v-show</span>=<span class="hljs-string">&quot;date&quot;</span>&gt;</span>You selected <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>{{date}}<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span>.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-week-numbers.vue --&gt;</span>
</code></pre>
<h2 id="with-dropdown">With dropdown <a class="header-anchor" href="#with-dropdown">&#128279;</a></h2>
<date-picker-dropdown-example ref="date-picker-dropdown-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">form</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-inline&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">dropdown</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-group&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;input-group&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;input-group-btn&quot;</span>&gt;</span>
          <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;dropdown-toggle&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">i</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;glyphicon glyphicon-calendar&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">i</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
        <span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
      <span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">slot</span>=<span class="hljs-string">&quot;dropdown&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">li</span>&gt;</span>
          <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span>/&gt;</span>
        <span class="hljs-tag">&lt;/<span class="hljs-name">li</span>&gt;</span>
      <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
    <span class="hljs-tag">&lt;/<span class="hljs-name">dropdown</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">form</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-dropdown-example.vue --&gt;</span>
</code></pre>
<h2 id="custom-date-classes">Custom date classes <a class="header-anchor" href="#custom-date-classes">&#128279;</a></h2>
<p>Use <code v-pre>date-class</code> to apply custom classes to each date button, which should be an function that:</p>
<ul>
<li>takes the date of button as the first param.</li>
<li>(0.33.0+) also with current month and year showing of the picker in the second param.</li>
<li>returns the class(es).</li>
</ul>
<p>See below example for detail usage, which has all sunday highlighted:</p>
<date-picker-custom-date-classes ref="date-picker-custom-date-classes"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;date&quot;</span> <span class="hljs-attr">:date-class</span>=<span class="hljs-string">&quot;dateClass&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">date</span>: <span class="hljs-literal">null</span>
      }
    },
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">dateClass</span> (date) {
        <span class="hljs-keyword">return</span> date.<span class="hljs-title function_">getDay</span>() === <span class="hljs-number">0</span> ? <span class="hljs-string">&#x27;btn-sunday&#x27;</span> : <span class="hljs-string">&#x27;&#x27;</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">style</span>&gt;</span><span class="language-css">
 <span class="hljs-selector-class">.btn-sunday</span><span class="hljs-selector-class">.btn-default</span> {<span class="hljs-attribute">background-color</span>: <span class="hljs-number">#ccc</span>}
</span><span class="hljs-tag">&lt;/<span class="hljs-name">style</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-custom-date-classes.vue --&gt;</span>
</code></pre>
<h2 id="custom-icons">Custom icons <a class="header-anchor" href="#custom-icons">&#128279;</a></h2>
<date-picker-icons-example ref="date-picker-icons-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">icon-control-left</span>=<span class="hljs-string">&quot;glyphicon glyphicon-triangle-left&quot;</span> <span class="hljs-attr">icon-control-right</span>=<span class="hljs-string">&quot;glyphicon glyphicon-triangle-right&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-icons-example.vue --&gt;</span>
</code></pre>
<h2 id="custom-locale">Custom locale <a class="header-anchor" href="#custom-locale">&#128279;</a></h2>
<p>Pass a locale object to <code v-pre>locale</code> for custom translations of month and weekday names, <code v-pre>clear-btn</code> and <code v-pre>today-btn</code> texts.</p>
<date-picker-locale ref="date-picker-locale"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">date-picker</span> <span class="hljs-attr">:locale</span>=<span class="hljs-string">&quot;localeHU&quot;</span> /&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">import</span> localeHU <span class="hljs-keyword">from</span> <span class="hljs-string">&#x27;uiv/src/locale/lang/hu-HU&#x27;</span>
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        localeHU
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- date-picker-locale.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="datepicker-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/datepicker/DatePicker.vue">DatePicker</a> <a class="header-anchor" href="#datepicker-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>v-model</code></td>
<td></td>
<td></td>
<td>✔</td>
<td>The selected date.</td>
</tr>
<tr>
<td><code v-pre>width</code></td>
<td>Number</td>
<td>270</td>
<td></td>
<td>The date-picker's width in px.</td>
</tr>
<tr>
<td><code v-pre>today-btn</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Show / hide the today button.</td>
</tr>
<tr>
<td><code v-pre>clear-btn</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Show / hide the clear button.</td>
</tr>
<tr>
<td><code v-pre>format</code></td>
<td>String</td>
<td>yyyy-MM-dd</td>
<td></td>
<td>The date format, will emit Date object if provided as empty string.</td>
</tr>
<tr>
<td><code v-pre>close-on-selected</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Close the date-picker dropdown after date selected.</td>
</tr>
<tr>
<td><code v-pre>limit-from</code></td>
<td></td>
<td></td>
<td></td>
<td>Anything that can convert to a valid Date object. E.g. <code v-pre>2017-01-01</code> or <code v-pre>new Date()</code>.</td>
</tr>
<tr>
<td><code v-pre>limit-to</code></td>
<td></td>
<td></td>
<td></td>
<td>Same as <code v-pre>limit-from</code>.</td>
</tr>
<tr>
<td><code v-pre>initial-view</code></td>
<td>String</td>
<td>d</td>
<td></td>
<td>Open the date-picker with specify view (one of <code v-pre>d</code> / <code v-pre>m</code> / <code v-pre>y</code>) on initial. Only works if the <code v-pre>v-model</code> is empty.</td>
</tr>
<tr>
<td><code v-pre>week-starts-with</code></td>
<td>Number</td>
<td>0</td>
<td></td>
<td>Starting day of the week. Support 0 (Sunday) ~ 6 (Saturday).</td>
</tr>
<tr>
<td><code v-pre>week-numbers</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Show week numbers of year.</td>
</tr>
<tr>
<td><code v-pre>date-parser</code></td>
<td>Function</td>
<td>Date.parse</td>
<td></td>
<td>Use this prop to replace the <code v-pre>Date.parse</code> call inside the component. Useful when The formatted String can not be correctly parsed to Date type by <code v-pre>Date.parse</code> (e.g. dd-MM-yyyy). For example: <code v-pre>dateParser (value) {return moment(value, 'DD-MM-YYYY').toDate().getTime()}</code></td>
</tr>
<tr>
<td><code v-pre>date-class</code></td>
<td>Function</td>
<td></td>
<td></td>
<td>The custom class callback function for each date. See above example section for details.</td>
</tr>
<tr>
<td><code v-pre>year-month-formatter</code></td>
<td>Function</td>
<td></td>
<td></td>
<td>The formatter function of year month label string on top of date view, with 2 params <code v-pre>year</code> and <code v-pre>month</code> (0-based), with the formatted string returned.</td>
</tr>
<tr>
<td><code v-pre>icon-control-left</code></td>
<td>String</td>
<td>glyphicon glyphicon-chevron-left</td>
<td></td>
<td>The arrow icon shown inside the <code v-pre>previous</code> button.</td>
</tr>
<tr>
<td><code v-pre>icon-control-right</code></td>
<td>String</td>
<td>glyphicon glyphicon-chevron-right</td>
<td></td>
<td>The arrow icon shown inside the <code v-pre>next</code> button.</td>
</tr>
<tr>
<td><code v-pre>locale</code></td>
<td>Object</td>
<td></td>
<td></td>
<td>(0.24.0+) The locale used for translating month and weekday names, clear-btn and today-btn texts.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><style>
 .btn-sunday.btn-default {background-color: #ccc}
</style><script>
  

  

  

  

  

  

  

  

  import localeHU from 'uiv/src/locale/lang/hu-HU'
  
export default {components:{'date-picker-example':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <date-picker v-model=\"date\"/>\n    <br/>\n    <alert type=\"info\" v-show=\"date\">You selected <b>{{date}}</b>.</alert>\n  </section>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-formats':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <date-picker v-model=\"date\" format=\"yyyy/MMMM/dd\"/>\n    <br/>\n    <alert type=\"info\" v-show=\"date\">You selected <b>{{date}}</b>.</alert>\n  </section>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-without-buttons':{"template":"<div class=\"markdown-live-example\">\n  <date-picker v-model=\"date\" :today-btn=\"false\" :clear-btn=\"false\"/>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-range-limit':{"template":"<div class=\"markdown-live-example\">\n  <date-picker v-model=\"date\" limit-from=\"2018-01-01\" limit-to=\"2019-01-01\"/>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-week-starts':{"template":"<div class=\"markdown-live-example\">\n  <date-picker v-model=\"date\" :week-starts-with=\"1\"/>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-week-numbers':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <date-picker v-model=\"date\" week-numbers/>\n    <br/>\n    <alert type=\"info\" v-show=\"date\">You selected <b>{{date}}</b>.</alert>\n  </section>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-dropdown-example':{"template":"<div class=\"markdown-live-example\">\n  <form class=\"form-inline\">\n    <dropdown class=\"form-group\">\n      <div class=\"input-group\">\n        <input class=\"form-control\" type=\"text\" v-model=\"date\">\n        <div class=\"input-group-btn\">\n          <btn class=\"dropdown-toggle\"><i class=\"glyphicon glyphicon-calendar\"></i></btn>\n        </div>\n      </div>\n      <template slot=\"dropdown\">\n        <li>\n          <date-picker v-model=\"date\"/>\n        </li>\n      </template>\n    </dropdown>\n  </form>\n</div>",
    data () {
      return {
        date: null
      }
    }
  },'date-picker-custom-date-classes':{"template":"<div class=\"markdown-live-example\">\n  <date-picker v-model=\"date\" :date-class=\"dateClass\"/>\n</div>",
    data () {
      return {
        date: null
      }
    },
    methods: {
      dateClass (date) {
        return date.getDay() === 0 ? 'btn-sunday' : ''
      }
    }
  },'date-picker-icons-example':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <date-picker icon-control-left=\"glyphicon glyphicon-triangle-left\" icon-control-right=\"glyphicon glyphicon-triangle-right\"/>\n  </section>\n</div>"},'date-picker-locale':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <date-picker :locale=\"localeHU\" />\n  </section>\n</div>",
    data () {
      return {
        localeHU
      }
    }
  }}}</script>